import React, { useState } from 'react';
import { FaFilter, FaSeedling, FaFlask, FaWarehouse, FaImages, FaCertificate } from 'react-icons/fa';

// --- Image Data Structure ---
interface GalleryItem {
    id: number;
    src: string;
    alt: string;
    category: 'process' | 'products' | 'farm' | 'certificates'; // 4 main categories
}

// --- Placeholder Images (Aap yahan apni high-resolution images ke paths dalen) ---
const GALLERY_DATA: GalleryItem[] = [
    // 1. Process Images
    { id: 1, src: "https://safeflex.org/wp-content/uploads/2021/03/Vermi-bed.jpg", alt: "Vermi-beds setup", category: 'process' },
    { id: 2, src: "https://thumbs.dreamstime.com/b/processing-organic-waste-compost-preparation-303576720.jpg", alt: "Organic waste preparation", category: 'process' },
    { id: 3, src: "https://i.ytimg.com/vi/-pMT5KzaDCw/maxresdefault.jpg", alt: "Compost sieving machine", category: 'process' },
    { id: 4, src: "https://www.wormfarmingsecrets.com/wp-content/uploads/2021/05/worm-composting-hands-1024x576.jpg", alt: "Earthworms in compost", category: 'process' },
    
    // 2. Product Images
    { id: 5, src: "https://mittisoul.com/wp-content/uploads/2018/05/1kg-vermi-compost-4.jpg", alt: "Packaged vermicompost bags", category: 'products' },
    { id: 6, src: "https://4.imimg.com/data4/NY/DV/MY-7114175/vermiwash-liquid-500x500.jpg", alt: "VermiWash liquid packaging", category: 'products' },
    { id: 7, src: "https://static.vecteezy.com/system/resources/previews/047/157/073/non_2x/close-up-of-hands-holding-rich-organic-compost-photo.jpeg", alt: "Close-up of premium compost", category: 'products' },
    
    // 3. Farm Images
    { id: 8, src: "https://images.picxy.com/cache/2020/8/1/189ebc5a658f0aa01ef0faf763aa11ff.jpg", alt: "General view of the facility", category: 'farm' },
    { id: 9, src: "https://hort.extension.wisc.edu/files/2016/09/composter-image.jpg", alt: "Worker managing compost beds", category: 'farm' },
    { id: 10, src: "https://cdn.mos.cms.futurecdn.net/Nezuh37DephBfwR68VZMyi.jpg", alt: "Storage area for ready compost", category: 'farm' },
    
    // 4. Certificates (Use your certificate image paths here)
    { id: 11, src: "https://timesofagriculture.in/wp-content/uploads/2023/12/Organic-Certification-.jpg", alt: "Organic Certification", category: 'certificates' },
    { id: 12, src: "https://www.omorganickitchen.com/wp-content/uploads/2023/08/sourcing-process-img2.jpg", alt: "Lab Test Report", category: 'certificates' },
];

const categories = [
    { name: 'All', icon: FaImages, filter: 'all' as const },
    { name: 'Process', icon: FaWarehouse, filter: 'process' as const },
    { name: 'Products', icon: FaSeedling, filter: 'products' as const },
    { name: 'Farm View', icon: FaFlask, filter: 'farm' as const },
    { name: 'Certificates', icon: FaCertificate, filter: 'certificates' as const },
];


const GalleryPage: React.FC = () => {
    const [activeFilter, setActiveFilter] = useState<'all' | 'process' | 'products' | 'farm' | 'certificates'>('all');

    const filteredData = GALLERY_DATA.filter(item => 
        activeFilter === 'all' ? true : item.category === activeFilter
    );

    return (
        // Adjusted padding for better mobile view
     <div className="bg-white py-12 sm:py-16 px-4 sm:px-8 mt-32">

            <div className="max-w-7xl mx-auto">
                
                {/* Header - Adjusted Text Size */}
                <header className="text-center mb-10 sm:mb-16">
                    {/* Scaled text: 4xl -> 5xl */}
                    <h1 className="text-4xl md:text-5xl font-extrabold text-gray-800 border-b-4 border-[#4f6a39] inline-block pb-2">
                        Our Photo Gallery
                    </h1>
                    {/* Scaled text: base -> xl */}
                    <p className="mt-4 text-base sm:text-xl text-gray-600 max-w-2xl mx-auto">
                        A visual journey into our commitment to quality, sustainable processes, and organic farming solutions.
                    </p>
                </header>

                {/* Filter Buttons - Ensured wrapping on mobile and centered layout */}
                <div className="flex flex-wrap justify-center gap-3 sm:gap-4 mb-10 sm:mb-12 p-3 sm:p-4 bg-gray-50 rounded-lg shadow-inner">
                    {/* Filter Icon visible only on larger screens */}
                    <FaFilter className="text-xl sm:text-2xl text-[#4f6a39] hidden md:block mt-2 flex-shrink-0" /> 
                    {categories.map(cat => (
                        <button
                            key={cat.filter}
                            onClick={() => setActiveFilter(cat.filter)}
                            // Reduced padding and text size on mobile for wrapping, added 'whitespace-nowrap'
                            className={`flex items-center space-x-1 sm:space-x-2 px-3 sm:px-5 py-2 rounded-full text-sm sm:text-base font-semibold transition-colors duration-200 whitespace-nowrap
                                ${activeFilter === cat.filter 
                                    ? 'bg-[#4f6a39] text-white shadow-lg' 
                                    : 'bg-white text-gray-700 hover:bg-gray-200 border border-gray-300'
                                }`}
                        >
                            <cat.icon className="text-base sm:text-lg" />
                            <span>{cat.name}</span>
                        </button>
                    ))}
                </div>

                {/* Gallery Grid - Responsive Grid Columns */}
                {/* Mobile: 1 Column, Tablet: 2 Columns, Desktop: 3 Columns, Large Desktop: 4 Columns */}
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
                    {filteredData.map(item => (
                        <div 
                            key={item.id} 
                            className="bg-gray-100 rounded-xl overflow-hidden shadow-lg group hover:shadow-2xl transition-transform duration-300 transform hover:scale-[1.02] cursor-pointer"
                        >
                            <img 
                                src={item.src} 
                                alt={item.alt}
                                // Ensured image height is consistent
                                className="w-full h-56 sm:h-64 object-cover transition-opacity duration-300 group-hover:opacity-90"
                            />
                            <div className="p-4">
                                <p className="text-base font-medium text-gray-700 capitalize">
                                    {item.alt}
                                </p>
                                <span className="text-sm text-[#4f6a39] font-semibold mt-1 block">
                                    {item.category.charAt(0).toUpperCase() + item.category.slice(1)}
                                </span>
                            </div>
                        </div>
                    ))}
                </div>

                {/* No Results Message */}
                {filteredData.length === 0 && (
                    <div className="text-center py-10">
                        <p className="text-xl text-gray-500">No images found in the selected category.</p>
                    </div>
                )}
            </div>
        </div>
    );
};

export default GalleryPage;